import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

import { setTitle } from "../redux/app/app.slice";
import { useAppDispatch } from "../hooks";
import useSidebar, { flattenSidebarLeaves, ISidebar } from "@/hooks/useSidebar";
import { BRAND, hexToRgba } from "@/utils";

function pathMatches(pathname: string, item: ISidebar): boolean {
  if (item.exact) {
    return pathname === item.path || pathname.startsWith(`${item.path}/`);
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function groupContainsPath(pathname: string, group: ISidebar): boolean {
  return (group.children ?? []).some((c) => pathMatches(pathname, c));
}

export function SidebarMenu({
  onSidebarClick,
  searchQuery = "",
}: {
  onSidebarClick?: Function;
  searchQuery?: string;
}) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const sidebar = useSidebar();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const leaf = flattenSidebarLeaves(sidebar)
      .filter((s) => pathMatches(location.pathname, s))
      .sort((a, b) => b.path.length - a.path.length)[0];
    if (leaf) {
      dispatch(setTitle(leaf.label));
    }
  }, [location.pathname, sidebar, dispatch]);

  // Auto-expand IMS (or any group) when a child route is active.
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const item of sidebar) {
        if (item.children?.length && groupContainsPath(location.pathname, item)) {
          next[item.path] = true;
        }
      }
      return next;
    });
  }, [location.pathname, sidebar]);

  const filteredSidebar = useMemo(() => {
    if (!searchQuery.trim()) return sidebar;
    const query = searchQuery.toLowerCase();
    return sidebar
      .map((item) => {
        if (item.children?.length) {
          const children = item.children.filter((c) =>
            c.label.toLowerCase().includes(query),
          );
          if (item.label.toLowerCase().includes(query)) {
            return item;
          }
          if (children.length) {
            return { ...item, children };
          }
          return null;
        }
        return item.label.toLowerCase().includes(query) ? item : null;
      })
      .filter(Boolean) as ISidebar[];
  }, [searchQuery, sidebar]);

  // While searching, keep matching groups expanded.
  useEffect(() => {
    if (!searchQuery.trim()) return;
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const item of filteredSidebar) {
        if (item.children?.length) next[item.path] = true;
      }
      return next;
    });
  }, [searchQuery, filteredSidebar]);

  const navBase =
    "flex p-3 w-full justify-between cursor-pointer items-center text-white transition-colors";
  const navActive = `${navBase} bg-[#234A7A] text-[#F8FAFC] font-semibold`;
  const childBase =
    "flex py-2.5 pl-9 pr-3 w-full cursor-pointer items-center text-white/90 text-sm transition-colors";
  const childActive = `${childBase} font-semibold`;

  return (
    <ul className="m-0 p-0 list-none mt-4 mb-4">
      {filteredSidebar.map((item) => {
        if (item.hidden) return null;
        const Icon = item.icon;

        if (item.children?.length) {
          const open = !!openGroups[item.path];
          const groupActive = groupContainsPath(location.pathname, item);
          return (
            <li key={item.path}>
              <button
                type="button"
                className={
                  groupActive
                    ? `${navBase} bg-[#234A7A]/80 text-[#F8FAFC] font-semibold`
                    : navBase
                }
                onClick={() =>
                  setOpenGroups((prev) => ({
                    ...prev,
                    [item.path]: !prev[item.path],
                  }))
                }
                aria-expanded={open}
              >
                <div className="flex items-center min-w-0">
                  <Icon
                    size={18}
                    strokeWidth={1.75}
                    className="shrink-0 opacity-90"
                    aria-hidden
                  />
                  <span className="text-sm ml-2 truncate">{item.label}</span>
                </div>
                <ChevronDown
                  size={16}
                  className={`shrink-0 opacity-70 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                  aria-hidden
                />
              </button>
              {open && (
                <ul className="m-0 p-0 list-none pb-1">
                  {item.children.map((child) => {
                    if (child.hidden) return null;
                    const ChildIcon = child.icon;
                    return (
                      <li key={child.path}>
                        <NavLink
                          to={child.path}
                          end={child.exact}
                          className={({ isActive }) =>
                            isActive
                              ? childActive
                              : `${childBase} hover:bg-white/5`
                          }
                          style={({ isActive }) =>
                            isActive
                              ? {
                                  backgroundColor: hexToRgba(BRAND.blue, 0.35),
                                  borderLeft: `3px solid ${BRAND.blue}`,
                                }
                              : { borderLeft: "3px solid transparent" }
                          }
                          onClick={() => onSidebarClick?.()}
                        >
                          <ChildIcon
                            size={16}
                            strokeWidth={1.75}
                            className="shrink-0 opacity-90"
                            aria-hidden
                          />
                          <span className="ml-2">{child.label}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        }

        return (
          <li key={item.path}>
            <NavLink
              to={item.path}
              end={item.exact}
              className={({ isActive }) => (isActive ? navActive : navBase)}
              onClick={() => onSidebarClick?.()}
            >
              <div className="flex items-center">
                <Icon
                  size={18}
                  strokeWidth={1.75}
                  className="shrink-0 opacity-90"
                  aria-hidden
                />
                <span className="text-sm ml-2">{item.label}</span>
              </div>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}
